// Enhance the existing Formie inputs so validation, conditions, and submissions
// continue to use the original checkboxes.
export default class CheckboxDropdown {
    constructor(field) {
        this.field = field;
        this.wrapper = field.querySelector('.form-floating');
        this.inputs = [...field.querySelectorAll('input[type="checkbox"]')];
        const label = field.querySelector('[data-formie-field-label]');
        this.panel = field.querySelector('[data-formie-field-options]');

        if (!this.wrapper || !label || !this.panel || !this.inputs.length) return;

        const id = this.inputs[0].id + '-dropdown';
        label.id = id + '-label';
        label.classList.add('checkbox-dropdown-legend');
        this.panel.id = id + '-options';
        this.panel.classList.add('checkbox-dropdown-panel');
        this.panel.classList.add('is-closed');
        this.panel.inert = true;
        this.panel.setAttribute('aria-hidden', 'true');
        this.panel.setAttribute('role', 'group');
        this.panel.setAttribute('aria-labelledby', label.id);

        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'input checkbox-dropdown-trigger';
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-controls', this.panel.id);
        this.trigger.setAttribute('aria-labelledby', label.id + ' ' + id + '-value');

        const floatingLabel = document.createElement('span');
        floatingLabel.className = 'checkbox-dropdown-label';
        floatingLabel.setAttribute('aria-hidden', 'true');
        floatingLabel.textContent = label.textContent.trim();
        this.summary = document.createElement('span');
        this.summary.id = id + '-value';
        this.summary.className = 'checkbox-dropdown-value';
        this.trigger.append(floatingLabel, this.summary);
        this.wrapper.classList.add('checkbox-dropdown');
        this.wrapper.insertBefore(this.trigger, this.panel);
        this.update();

        this.trigger.addEventListener('click', () => this.setOpen(!this.isOpen));
        this.trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.setOpen(true);
                this.inputs.find((input) => !input.disabled)?.focus();
            }
        });
        this.wrapper.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.setOpen(false);
                this.trigger.focus();
            }
        });
        this.wrapper.addEventListener('focusout', (event) => {
            if (!this.wrapper.contains(event.relatedTarget)) this.setOpen(false);
        });
        document.addEventListener('click', (event) => {
            if (!this.wrapper.contains(event.target)) this.setOpen(false);
        });
        this.panel.addEventListener('change', () => queueMicrotask(() => this.update()));
        this.panel.addEventListener('invalid', () => this.setOpen(true), true);
        const form = this.field.closest('form');
        form.addEventListener('formie:validator:show-error', (event) => {
            if (this.inputs.includes(event.target)) {
                // Let Formie focus the first invalid field without leaving it inert.
                this.setOpen(true);
                this.syncValidation();
            }
        });
        form.addEventListener('formie:validator:clear-error', (event) => {
            if (this.inputs.includes(event.target)) this.syncValidation();
        });
        form.addEventListener('formie:submit:result', () => this.syncValidation());
        form.addEventListener('reset', () => {
            this.setOpen(false);
            this.trigger.removeAttribute('aria-invalid');
            setTimeout(() => this.update(), 0);
        });
    }

    update() {
        this.syncValidation();
        const selected = this.inputs
            .filter((input) => input.checked && !input.hasAttribute('data-checkbox-toggle') && !input.hasAttribute('data-formie-checkbox-toggle'))
            .map((input) => [...this.panel.querySelectorAll('label')]
                .find((label) => label.htmlFor === input.id)?.textContent.trim() || input.value);
        this.summary.textContent = selected.length ? selected.join(', ') : 'Select all that apply';
        this.summary.classList.toggle('is-placeholder', !selected.length);
        this.trigger.title = selected.join(', ');
    }

    syncValidation() {
        const invalid = this.field.matches('[data-formie-field-has-error="true"]')
            || !!this.field.querySelector('[aria-invalid="true"]:not(.checkbox-dropdown-trigger)');
        if (invalid) this.trigger.setAttribute('aria-invalid', 'true');
        else this.trigger.removeAttribute('aria-invalid');
        const errors = this.field.querySelector('[data-formie-field-errors]');
        if (invalid && errors?.id) this.trigger.setAttribute('aria-describedby', errors.id);
        else this.trigger.removeAttribute('aria-describedby');
    }

    setOpen(open) {
        this.panel.classList.toggle('is-closed', !open);
        this.panel.inert = !open;
        this.panel.setAttribute('aria-hidden', String(!open));
        this.trigger.setAttribute('aria-expanded', String(open));
    }

    get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
    }
}
